import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import CardDetail from './CardDetail.js';
import { getCardName, getUILoc, getPlayerName } from '../utils/cardUtils.js';
import '../App.css';

const GameLogOverlay = ({ gameState, cardDefs, logEntries, getFilteredEntries, onClose }) => {
    const [filterType, setFilterType] = useState('all');
    const [selectedCard, setSelectedCard] = useState(null);
    const logContentRef = useRef(null);
    
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [userScrolledUp, setUserScrolledUp] = useState(false);
    
    useEffect(() => {
        const element = logContentRef.current;
        if (!element) return;
        
        const handleScroll = () => {
            const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 50;
            setUserScrolledUp(!isNearBottom);
        };
        
        element.addEventListener('scroll', handleScroll);
        return () => element.removeEventListener('scroll', handleScroll);
    }, []);
    
    useEffect(() => {
        if (logContentRef.current && logEntries && gameState) {
            const filteredEntries = getFilteredEntries(filterType);
            if (filteredEntries) {
                const element = logContentRef.current;
                if (isInitialLoad) {
                    element.scrollTop = element.scrollHeight;
                    setIsInitialLoad(false);
                    return;
                }
                if (!userScrolledUp) {
                    element.scrollTop = element.scrollHeight;
                }
            }
        }
    }, [logEntries, gameState, filterType, isInitialLoad, userScrolledUp, getFilteredEntries]);

    if (!logEntries || !gameState) return null;

    const filteredEntries = getFilteredEntries(filterType);
    
    const logStats = {
        total: logEntries.length,
        effect: logEntries.filter(entry => entry.source === 'effect_queue' || entry.type === 'effect').length,
        progress: logEntries.filter(entry => entry.source === 'game_log').length,
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    const getCardFromLogEntry = (entry) => {
        if (!entry || !gameState) return null;

        if (entry.details && entry.details.cardId && gameState.all_card_instances) {
            const card = gameState.all_card_instances[entry.details.cardId];
            if (card) return card;
        }

        if (entry.sourceCardInfo && entry.sourceCardInfo.instanceId && gameState.all_card_instances) {
            const card = gameState.all_card_instances[entry.sourceCardInfo.instanceId];
            if (card) return card;
        }
        
        return null;
    };

    const handleLogEntryClick = (entry, e) => {
        e.stopPropagation();
        
        if (entry.params && entry.params.isHidden) {
            return;
        }

        const card = getCardFromLogEntry(entry);
        if (card) setSelectedCard(card);
    };

    const formatLogEntry = (entry, index) => {
        if (entry.messageKey) {
            const params = { ...entry.params };
            
            if (params.cardId) params.cardName = getCardName(params.cardId);
            if (params.targetCardId) params.targetCardName = getCardName(params.targetCardId);
            if (params.cardIds) params.exposedCardNames = params.cardIds.map(id => getCardName(id)).join(', ');
            if (params.playerType) params.playerName = getPlayerName(params.playerType);
            
            if (params.sourceZoneKey) params.sourceZone = getUILoc(params.sourceZoneKey);
            if (params.destZoneKey) params.destZone = getUILoc(params.destZoneKey);
            if (params.originalTypeKey) params.originalType = getUILoc(params.originalTypeKey);

            if (params.isHidden) params.cardName = " ---- ";

            const description = getUILoc(entry.messageKey, params);
            
            const playerName = entry.params?.playerName || (entry.playerId === 'PLAYER1' ? getPlayerName('SELF') : entry.playerId === 'PLAYER2' ? getPlayerName('OPPONENT') : getUILoc('LOG.SYSTEM'));
            
            let sourceName = '';
            if (entry.sourceCardInfo) {
                const ownerName = getPlayerName(entry.sourceCardInfo.ownerId === 'PLAYER1' ? 'SELF' : 'OPPONENT');
                const cardName = getCardName(entry.sourceCardInfo.cardId);
                sourceName = getUILoc('LOG.SOURCE_FORMAT', { ownerName, cardName });
            } else {
                sourceName = getUILoc('LOG.SYSTEM');
            }

            return (
                <div className="enhanced-log-entry">
                    <span className="log-player">[{playerName}]</span>
                    <span className="log-source">[{sourceName}]</span>
                    <span className="log-description">{description}</span>
                </div>
            );
        }
        
        const description = entry.description || entry.message || `効果 ${index + 1}`;
        const playerName = entry.playerName || getUILoc('LOG.SYSTEM');
        const sourceName = entry.sourceCard || '';

        return (
            <div className="enhanced-log-entry">
                <span className="log-player">[{playerName}]</span>
                {sourceName && <span className="log-source">[{sourceName}]</span>}
                <span className="log-description">{description}</span>
            </div>
        );
    };

    const filterOptions = [
        { value: 'all', label: getUILoc('LABELS.FILTER_ALL') },
        { value: 'card_play', label: getUILoc('LOG.FILTER_CARD_PLAY') },
        { value: 'damage', label: getUILoc('LOG.FILTER_DAMAGE') },
        { value: 'resource', label: getUILoc('LOG.FILTER_RESOURCE') },
        { value: 'card_move', label: getUILoc('LOG.FILTER_CARD_MOVE') }
    ];

    return createPortal(
        <div className="game-log-overlay" onClick={handleOverlayClick}>
            {selectedCard && (
                <CardDetail 
                    card={selectedCard} 
                    cardDefs={cardDefs || gameState.cardDefs}
                    onClose={() => setSelectedCard(null)} 
                />
            )}
            
            <div className="game-log-modal" onClick={e => e.stopPropagation()}>
                <div className="game-log-header">
                    <h2>{getUILoc('LABELS.HISTORY_LOG')}</h2>
                    <div className="log-controls">
                        {userScrolledUp && (
                            <button className="scroll-to-bottom-button" onClick={() => {
                                if (logContentRef.current) {
                                    logContentRef.current.scrollTop = logContentRef.current.scrollHeight;
                                    setUserScrolledUp(false);
                                }
                            }}>↓ {getUILoc('LABELS.LATEST')}</button>
                        )}
                        <button className="close-button" onClick={onClose}>×</button>
                    </div>
                </div>
                
                <div className="log-filter-bar">
                    {filterOptions.map(option => (
                        <button
                            key={option.value}
                            className={`filter-button ${filterType === option.value ? 'active' : ''}`}
                            onClick={() => setFilterType(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                
                <div className="game-log-content" ref={logContentRef}>
                    {filteredEntries.length === 0 ? (
                        <div className="no-log">
                            {filterType === 'all' ? getUILoc('MESSAGES.NO_LOG') : getUILoc('MESSAGES.NO_MATCHING_LOG')}
                        </div>
                    ) : (
                        <div className="log-entries">
                            {filteredEntries.map((entry, index) => {
                                const card = getCardFromLogEntry(entry);
                                const isClickable = !!card;
                                const playerClass = entry.playerId ? `log-entry-${entry.playerId}` : '';
                                
                                return (
                                    <div 
                                        key={entry.id || index} 
                                        className={`log-entry ${playerClass} ${isClickable ? 'clickable-log-entry' : ''}`}
                                        onClick={isClickable ? (e) => handleLogEntryClick(entry, e) : undefined}
                                    >
                                        <div className="log-index">{index + 1}</div>
                                        <div className="log-message">
                                            {formatLogEntry(entry, index)}
                                        </div>
                                        {entry.timestamp && (
                                            <div className="log-timestamp">
                                                {new Date(entry.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', second:'2-digit'})}
                                            </div>
                                        )}
                                        {isClickable && <div className="log-click-hint">📋 {getUILoc('BUTTONS.DETAIL')}</div>}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                
                <div className="game-log-footer">
                    <div className="log-stats">
                        {getUILoc('LABELS.BASIC_INFO')}: {logStats.total} / {logStats.effect} / {logStats.progress}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default GameLogOverlay;
